/**
 * Art preloading (docs/design/06 §6: "preloaded for the player's deck at match start"). The Play hub
 * remembers the printings of the deck a match was started with; the match page warms the image cache for
 * them, and for every face visible in the first view, at board size (and hand size for your cards).
 */
import { type ArtWidth, type Catalog, artSources } from '@play/catalog';
import type { DeckList, PlayerView } from '@play/protocol';
import { artConfig } from './env';

const KEY = 'runebound.play.deckArt';
const seen = new Set<string>();

/**
 * Remembers the printings of the deck a match is about to start with (session only). The table's deck list is a
 * separate, match-keyed snapshot (`deck-reference.ts`).
 */
export function rememberDeckForPreload(deck: DeckList): void {
  const ids = [
    deck.legend,
    ...(deck.champion ? [deck.champion] : []),
    ...deck.main,
    ...deck.runes,
    ...deck.battlefields,
  ].map((c) => c.printingId ?? c.cardId);
  try {
    sessionStorage.setItem(KEY, JSON.stringify([...new Set(ids)]));
  } catch {
    // Storage blocked: images still load lazily.
  }
}

function warm(catalog: Catalog, printingOrCard: string, width: ArtWidth): void {
  const printing = catalog.printing(printingOrCard) ?? catalog.plainPrinting(printingOrCard);
  if (!printing) return;
  const url = artSources(printing, width, artConfig)[0];
  if (!url || seen.has(url)) return;
  seen.add(url);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
}

/** Warms the cache for the remembered deck and the faces in `view`. */
export function preloadMatchArt(catalog: Catalog, view: PlayerView): void {
  try {
    const raw = sessionStorage.getItem(KEY);
    const ids: unknown = raw ? JSON.parse(raw) : [];
    if (Array.isArray(ids)) for (const id of ids) if (typeof id === 'string') warm(catalog, id, 372);
  } catch {
    // Unreadable: skip.
  }
  for (const o of Object.values(view.objects)) {
    if (o.card?.printingId) warm(catalog, o.card.printingId, 240);
  }
}

const previewQueue: (() => void)[] = [];
const previewQueued = new Set<string>();
let previewPumping = false;

/** Runs the queued preview warms a few at a time when the browser is idle, so they never compete with the board. */
function pumpPreview(): void {
  if (previewPumping) return;
  previewPumping = true;
  const idle: (cb: () => void) => void =
    typeof requestIdleCallback === 'function'
      ? (cb) => requestIdleCallback(cb, { timeout: 1500 })
      : (cb) => setTimeout(cb, 200);
  const step = () => {
    for (let i = 0; i < 3 && previewQueue.length > 0; i++) previewQueue.shift()?.();
    if (previewQueue.length > 0) idle(step);
    else previewPumping = false;
  };
  idle(step);
}

/**
 * Warms the hover preview's large art (744px) for every face you can see, in idle time (QoL 2026-10-16: the first
 * hover of a card no longer waits for its large image). Faces already warmed are skipped, so it is cheap to call on
 * every update.
 */
export function preloadPreviewArt(catalog: Catalog, view: PlayerView): void {
  for (const o of Object.values(view.objects)) {
    const id = o.card?.printingId ?? o.card?.cardId;
    if (!id || previewQueued.has(id)) continue;
    previewQueued.add(id);
    previewQueue.push(() => {
      warm(catalog, id, 744);
    });
  }
  pumpPreview();
}
